// src/pages/system/Nodes.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Chip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import GeographicMap from '../../components/maps/GeographicMap';

const NodeForm = ({ open, onClose, onSubmit, node = null, zones = [], routers = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    zone_id: '',
    router_id: '',
    coordinates: '',
    address: '',
    comments: ''
  });
  const [errors, setErrors] = useState({});
  const [mapOpen, setMapOpen] = useState(false);
  const [tempCoords, setTempCoords] = useState('');

  useEffect(() => {
    if (node) {
      setFormData({
        name: node.name || '',
        zone_id: node.zone_id || '',
        router_id: node.router_id || '',
        coordinates: node.coordinates || '',
        address: node.address || '',
        comments: node.comments || ''
      });
    } else {
      setFormData({
        name: '',
        zone_id: '',
        router_id: '',
        coordinates: '',
        address: '',
        comments: ''
      });
    }
    setErrors({});
  }, [node, open]);

  // Sincronizar coordenadas temporales cuando se abre el mapa
  useEffect(() => {
    if (mapOpen) {
      setTempCoords(formData.coordinates);
    }
  }, [mapOpen, formData.coordinates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMapConfirm = () => {
    setFormData(prev => ({ ...prev, coordinates: tempCoords }));
    setMapOpen(false);
  };

  // Mostrar todos los routers disponibles en el sistema
  const filteredRouters = routers;

  const validate = () => {
    const tempErrors = {};
    if (!formData.name.trim()) {
      tempErrors.name = 'El nombre del nodo es obligatorio';
    }
    if (formData.coordinates) {
      const parts = formData.coordinates.split(',').map(c => c.trim());
      if (parts.length !== 2 || isNaN(parseFloat(parts[0])) || isNaN(parseFloat(parts[1]))) {
        tempErrors.coordinates = 'Formato de coordenadas inválido. Debe ser: lat, lng';
      }
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {node ? 'Editar Nodo / Torre' : 'Nuevo Nodo / Torre'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4" sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nombre del Nodo / Torre"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="zone-select-label">Zona</InputLabel>
              <Select
                labelId="zone-select-label"
                name="zone_id"
                value={formData.zone_id}
                label="Zona"
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Sin zona asignada</em>
                </MenuItem>
                {zones.map((z) => (
                  <MenuItem key={z.id} value={z.id}>
                    {z.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel id="router-select-label">Router Administrador</InputLabel>
              <Select
                labelId="router-select-label"
                name="router_id"
                value={formData.router_id}
                label="Router Administrador"
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Sin router asignado</em>
                </MenuItem>
                {filteredRouters.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Coordenadas GPS"
              name="coordinates"
              placeholder="-12.046374, -77.042793"
              value={formData.coordinates}
              onChange={handleChange}
              error={!!errors.coordinates}
              helperText={errors.coordinates || "Formato: lat, lng (ejemplo: -12.046, -77.042)"}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Seleccionar en el Mapa">
                      <IconButton
                        edge="end"
                        color="primary"
                        onClick={() => setMapOpen(true)}
                      >
                        <MapIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              label="Dirección"
              name="address"
              value={formData.address}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Comentarios"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              multiline
              rows={3}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" color="primary">
            Guardar
          </Button>
        </DialogActions>
      </form>

      {/* Diálogo del Selector de Mapa */}
      <Dialog 
        open={mapOpen} 
        onClose={() => setMapOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Seleccionar Ubicación del Nodo</Typography>
          {tempCoords && (
            <Chip 
              label={`Coordenadas: ${tempCoords}`} 
              color="primary" 
              size="small" 
              sx={{ fontWeight: 600 }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
          <GeographicMap
            mode="selection"
            selectedCoordinates={tempCoords}
            onCoordinateSelect={(coords) => setTempCoords(coords)}
            height="100%"
            hideHeader={true}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setMapOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleMapConfirm} 
            variant="contained" 
            color="primary"
            disabled={!tempCoords}
          >
            Confirmar Coordenadas
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const Nodes = () => {
  const [nodes, setNodes] = useState([]);
  const [zones, setZones] = useState([]);
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchNodes();
    fetchZones();
    fetchRouters();
  }, []);

  const fetchNodes = async () => {
    try {
      const response = await fetch('/api/nodes', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Error al obtener nodos');
      const data = await response.json();
      setNodes(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching nodes:', error);
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const response = await fetch('/api/zones', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Error al obtener zonas');
      const data = await response.json();
      setZones(data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const fetchRouters = async () => {
    try {
      const response = await fetch('/api/routers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Error al obtener routers');
      const data = await response.json();
      setRouters(data);
    } catch (error) {
      console.error('Error fetching routers:', error);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setErrorMessage('');
      const method = selectedNode ? 'PUT' : 'POST';
      const url = selectedNode
        ? `/api/nodes/${selectedNode.id}`
        : '/api/nodes';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al guardar el nodo');
      }

      fetchNodes();
      setOpenForm(false);
      setSelectedNode(null);
    } catch (error) {
      console.error('Error saving node:', error);
      setErrorMessage(error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      setErrorMessage('');
      const response = await fetch(`/api/nodes/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al eliminar el nodo');
      }

      fetchNodes();
      setDeleteConfirmOpen(false);
      setNodeToDelete(null);
    } catch (error) {
      console.error('Error deleting node:', error);
      alert(error.message);
      setDeleteConfirmOpen(false);
      setNodeToDelete(null);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Nombre del Nodo / Torre', width: 200 },
    { field: 'zone_name', headerName: 'Zona', width: 150, valueGetter: (params) => params.row.zone_name || 'Sin zona' },
    { field: 'router_name', headerName: 'Router Admin', width: 160, valueGetter: (params) => params.row.router_name || 'Sin router' },
    { field: 'coordinates', headerName: 'Coordenadas', width: 180 },
    { field: 'address', headerName: 'Dirección', width: 200 },
    { field: 'apCount', headerName: 'APs Sectoriales', width: 140, type: 'number' },
    { field: 'comments', headerName: 'Comentarios', width: 250 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedNode(params.row);
                setOpenForm(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setNodeToDelete(params.row);
                setDeleteConfirmOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      {/* Cabecera Premium */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Nodos / Torres Físicas
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
            Gestione las torres de distribución y nodos de repetición del WISP.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedNode(null);
            setOpenForm(true);
            setErrorMessage('');
          }}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            py: 1.2,
            backgroundColor: '#6366f1',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
            '&:hover': { backgroundColor: '#4f46e5' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Nuevo Nodo
        </Button>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '16px' }}>
          {errorMessage}
        </Alert>
      )}

      {/* Tabla Premium */}
      <Paper
        sx={{
          borderRadius: '24px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white',
          p: 1
        }}
      >
        <DataGrid
          rows={nodes}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          sx={{
            border: 'none',
            fontFamily: 'Inter, sans-serif',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#475569',
                fontSize: '0.85rem'
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)',
              color: '#334155',
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                transform: 'scale(1.002)',
              }
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
              }
            },
          }}
        />
      </Paper>

      <NodeForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedNode(null);
          setErrorMessage('');
        }}
        onSubmit={handleSubmit}
        node={selectedNode}
        zones={zones}
        routers={routers}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación de Nodo</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar el nodo/torre "{nodeToDelete?.name}"?
          {parseInt(nodeToDelete?.apCount || 0) > 0 && (
            <Typography color="error" className="mt-2" sx={{ fontWeight: 'bold' }}>
              ¡Advertencia! Este nodo tiene {nodeToDelete.apCount} antenas sectoriales (APs) asignadas. Debe moverlas o eliminarlas antes de poder eliminar el nodo.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => handleDelete(nodeToDelete.id)}
            color="error"
            variant="contained"
            disabled={parseInt(nodeToDelete?.apCount || 0) > 0}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Nodes;
